import { useEffect, useRef } from 'react';

export interface SelectionSpaceShortcutProps {
  /** The sheet wrapper; the shortcut only reacts to keys pressed inside it. */
  container: HTMLElement | null;
  enabled: boolean;
  /** Runs the bulk toggle and reports whether it handled the press. */
  onSpace: () => boolean;
}

const INTERACTIVE_SELECTOR = [
  'input:not([type="checkbox"])',
  'textarea',
  'select',
  'button',
  '[role="button"]',
  '[role="combobox"]',
  '[contenteditable="true"]',
].join(',');

// Space belongs to the focused editor or control, never to the bulk toggle.
const isInteractiveTarget = (target: EventTarget | null) => {
  const element = target as Element | null;
  return typeof element?.closest === 'function' && !!element.closest(INTERACTIVE_SELECTOR);
};

/**
 * Space over a multi-row selection runs the page's bulk toggle instead of the per-cell checkbox.
 * A handled press also swallows its keyup, which is where the cell checkbox would toggle back.
 */
const useSelectionSpaceShortcut = ({ container, enabled, onSpace }: SelectionSpaceShortcutProps) => {
  const onSpaceRef = useRef(onSpace);
  onSpaceRef.current = onSpace;

  useEffect(() => {
    if (!container || !enabled) {
      return undefined;
    }

    const { ownerDocument } = container;
    let handled = false;
    const isSheetSpace = (event: KeyboardEvent) =>
      event.code === 'Space' && container.contains(event.target as Node) && !isInteractiveTarget(event.target);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isSheetSpace(event) || event.repeat) {
        return;
      }
      handled = onSpaceRef.current();
      if (handled) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || !handled) {
        return;
      }
      handled = false;
      event.preventDefault();
      event.stopPropagation();
    };

    ownerDocument.addEventListener('keydown', handleKeyDown, true);
    ownerDocument.addEventListener('keyup', handleKeyUp, true);
    return () => {
      ownerDocument.removeEventListener('keydown', handleKeyDown, true);
      ownerDocument.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [container, enabled]);
};

export default useSelectionSpaceShortcut;
